import { useState } from "react";
import { useParams, Link } from "@tanstack/react-router";
import { trpc } from "@/lib/trpc";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Loader2,
  ChevronLeft,
  ChevronRight,
  Calendar,
  Clock,
  Dumbbell,
  Users,
  Wrench,
  X,
} from "lucide-react";
import { toast } from "sonner";

const categoryIcons = {
  SPORT: Dumbbell,
  SOCIAL: Users,
  SERVICE: Wrench,
};

const categoryLabels = {
  SPORT: "Deporte",
  SOCIAL: "Social",
  SERVICE: "Servicio",
};

function formatTime(date: Date | string, timeZone?: string) {
  return new Date(date).toLocaleTimeString("es-AR", {
    hour: "numeric",
    minute: "2-digit",
    hour12: false,
    timeZone,
  });
}

function formatDate(date: Date | string, timeZone?: string) {
  return new Date(date).toLocaleDateString("es-AR", {
    weekday: "short",
    month: "short",
    day: "numeric",
    timeZone,
  });
}

function formatDateForInput(date: Date) {
  return date.toISOString().split("T")[0];
}

interface TimeSlot {
  startTime: string;
  endTime: string;
  isAvailable: boolean;
  isReservedByMe: boolean;
}

export function AmenityDetailPage() {
  const { amenityId } = useParams({ from: "/amenities/$amenityId" });
  const utils = trpc.useUtils();

  const [selectedDate, setSelectedDate] = useState(() => {
    const today = new Date();
    return formatDateForInput(today);
  });

  const [selectedSlot, setSelectedSlot] = useState<TimeSlot | null>(null);
  const [bookingDialogOpen, setBookingDialogOpen] = useState(false);
  const [cancelReservationId, setCancelReservationId] = useState<string | null>(
    null,
  );

  const { data: amenity, isLoading } =
    trpc.amenity.getAmenityWithSlots.useQuery({
      amenityId,
      date: selectedDate,
    });

  const createReservation = trpc.amenity.createReservation.useMutation({
    onSuccess: (data) => {
      utils.amenity.getAmenityWithSlots.invalidate();
      utils.amenity.getAmenities.invalidate();
      setBookingDialogOpen(false);
      setSelectedSlot(null);
      toast.success(
        data.status === "CONFIRMED"
          ? "¡Reserva confirmada!"
          : "Reserva enviada para aprobación",
      );
    },
    onError: (error) => {
      setBookingDialogOpen(false);
      setSelectedSlot(null);
      toast.error(error.message);
    },
  });

  const cancelReservation = trpc.amenity.cancelReservation.useMutation({
    onSuccess: () => {
      utils.amenity.getAmenityWithSlots.invalidate();
      utils.amenity.getAmenities.invalidate();
      toast.success("Reserva cancelada");
    },
    onError: (error) => {
      toast.error(error.message);
    },
  });

  const handleDateChange = (days: number) => {
    const current = new Date(selectedDate);
    current.setDate(current.getDate() + days);
    setSelectedDate(formatDateForInput(current));
    setSelectedSlot(null);
  };

  const handleSlotClick = (slot: TimeSlot) => {
    if (!slot.isAvailable) return;
    setSelectedSlot(slot);
    setBookingDialogOpen(true);
  };

  const handleConfirmBooking = () => {
    if (!selectedSlot) return;

    createReservation.mutate({
      amenityId,
      startTime: selectedSlot.startTime,
      endTime: selectedSlot.endTime,
    });
  };

  if (isLoading) {
    return (
      <div className="container mx-auto max-w-4xl px-4 py-6 md:px-6 md:py-10">
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      </div>
    );
  }

  if (!amenity) {
    return (
      <div className="container mx-auto max-w-4xl px-4 py-6 md:px-6 md:py-10">
        <p className="text-muted-foreground">Amenity no encontrado.</p>
        <Link to="/amenities">
          <Button variant="outline" className="mt-4">
            <ChevronLeft className="mr-2 h-4 w-4" />
            Volver a Amenities
          </Button>
        </Link>
      </div>
    );
  }

  const CategoryIcon = categoryIcons[amenity.category];
  const tz = amenity.community.timezone;
  const today = formatDateForInput(new Date());
  const isPastDate = selectedDate < today;
  const maxDate = (() => {
    const d = new Date();
    d.setDate(d.getDate() + amenity.bookingRules.advanceBookingDays);
    return formatDateForInput(d);
  })();
  const isAtMaxDate = selectedDate >= maxDate;

  return (
    <div className="container mx-auto max-w-4xl px-4 py-6 md:px-6 md:py-10">
      {/* Header */}
      <div className="mb-6">
        <Link
          to="/amenities"
          className="mb-4 inline-flex items-center text-sm text-muted-foreground hover:text-foreground"
        >
          <ChevronLeft className="mr-1 h-4 w-4" />
          Volver a Amenities
        </Link>

        <div className="flex items-center gap-3">
          <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-muted">
            <CategoryIcon className="h-6 w-6" />
          </div>
          <div>
            <h1 className="text-2xl font-bold">{amenity.name}</h1>
            <div className="flex items-center gap-2">
              <Badge variant="secondary">
                {categoryLabels[amenity.category]}
              </Badge>
              {amenity.requiresApproval && (
                <Badge variant="outline">Requiere aprobación</Badge>
              )}
              <span className="text-sm text-muted-foreground">
                {amenity.community.name}
              </span>
            </div>
          </div>
        </div>
      </div>

      <div className="grid gap-6 lg:grid-cols-3">
        {/* Time Slots */}
        <div className="lg:col-span-2">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>Seleccioná un horario</CardTitle>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="icon"
                    aria-label="Día anterior"
                    onClick={() => handleDateChange(-1)}
                    disabled={selectedDate <= today}
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </Button>
                  <Input
                    type="date"
                    value={selectedDate}
                    onChange={(e) => {
                      setSelectedDate(e.target.value);
                      setSelectedSlot(null);
                    }}
                    min={today}
                    max={maxDate}
                    className="w-auto"
                  />
                  <Button
                    variant="outline"
                    size="icon"
                    aria-label="Día siguiente"
                    onClick={() => handleDateChange(1)}
                    disabled={isAtMaxDate}
                  >
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              </div>
              <CardDescription>
                {formatDate(selectedDate + "T12:00:00", tz)}
              </CardDescription>
            </CardHeader>
            <CardContent>
              {isPastDate ? (
                <p className="py-8 text-center text-muted-foreground">
                  No se pueden reservar horarios pasados
                </p>
              ) : amenity.slots.length === 0 ? (
                <p className="py-8 text-center text-muted-foreground">
                  No hay horarios disponibles este día
                </p>
              ) : (
                <div className="grid grid-cols-3 gap-2 sm:grid-cols-4 md:grid-cols-5">
                  {amenity.slots.map((slot, index) => {
                    const isPast = new Date(slot.startTime) <= new Date();
                    const isAvailable = slot.isAvailable && !isPast;

                    return (
                      <button
                        key={index}
                        onClick={() => isAvailable && handleSlotClick(slot)}
                        disabled={!isAvailable}
                        className={`
                          rounded-xl border px-3 py-2 text-sm font-medium transition-colors
                          ${
                            slot.isReservedByMe
                              ? "border-primary bg-primary/10 text-primary"
                              : isAvailable
                                ? "border-border bg-background hover:border-primary hover:bg-primary/5"
                                : "cursor-not-allowed border-muted bg-muted/50 text-muted-foreground"
                          }
                        `}
                      >
                        {formatTime(slot.startTime, tz)}
                      </button>
                    );
                  })}
                </div>
              )}

              {/* Legend */}
              <div className="mt-4 flex flex-wrap gap-4 text-sm">
                <div className="flex items-center gap-2">
                  <div className="h-4 w-4 rounded border border-border bg-background" />
                  <span className="text-muted-foreground">Disponible</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="h-4 w-4 rounded border border-primary bg-primary/10" />
                  <span className="text-muted-foreground">Tu reserva</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="h-4 w-4 rounded border border-muted bg-muted/50" />
                  <span className="text-muted-foreground">No disponible</span>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* My Reservations */}
        <div>
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Tus reservas</CardTitle>
            </CardHeader>
            <CardContent>
              {amenity.myReservations.length === 0 ? (
                <p className="py-4 text-center text-sm text-muted-foreground">
                  No hay próximas reservas
                </p>
              ) : (
                <div className="space-y-3">
                  {amenity.myReservations.map((reservation) => (
                    <div
                      key={reservation.id}
                      className="rounded-xl border bg-muted/30 p-3"
                    >
                      <div className="flex items-start justify-between">
                        <div>
                          <div className="flex items-center gap-2">
                            <Calendar className="h-4 w-4 text-muted-foreground" />
                            <span className="text-sm font-medium">
                              {formatDate(reservation.startTime, tz)}
                            </span>
                          </div>
                          <div className="mt-1 flex items-center gap-2">
                            <Clock className="h-4 w-4 text-muted-foreground" />
                            <span className="text-sm text-muted-foreground">
                              {formatTime(reservation.startTime, tz)} -{" "}
                              {formatTime(reservation.endTime, tz)}
                            </span>
                          </div>
                          <Badge
                            variant={
                              reservation.status === "CONFIRMED"
                                ? "default"
                                : reservation.status === "PENDING"
                                  ? "secondary"
                                  : "destructive"
                            }
                            className="mt-2 text-xs"
                          >
                            {reservation.status.toLowerCase()}
                          </Badge>
                        </div>

                        {(reservation.status === "CONFIRMED" ||
                          reservation.status === "PENDING") && (
                          <Button
                            variant="ghost"
                            size="icon"
                            aria-label="Cancelar reserva"
                            onClick={() =>
                              setCancelReservationId(reservation.id)
                            }
                            disabled={
                              cancelReservation.isPending &&
                              cancelReservation.variables?.reservationId ===
                                reservation.id
                            }
                          >
                            {cancelReservation.isPending &&
                            cancelReservation.variables?.reservationId ===
                              reservation.id ? (
                              <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                              <X className="h-4 w-4" />
                            )}
                          </Button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Booking Rules */}
          <Card className="mt-4">
            <CardHeader>
              <CardTitle className="text-base">Info de reserva</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2 text-sm text-muted-foreground">
              <p>
                Duración del turno: {amenity.bookingRules.slotDurationMinutes}{" "}
                minutos
              </p>
              <p>
                Máximo por reserva:{" "}
                {amenity.bookingRules.slotDurationMinutes *
                  amenity.bookingRules.maxSlotsPerBooking}{" "}
                minutos
              </p>
              <p>
                Reservá hasta {amenity.bookingRules.advanceBookingDays} días de
                anticipación
              </p>
              <p>
                Cancelá con {amenity.bookingRules.cancellationHours}+ horas de
                anticipación
              </p>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Booking Confirmation Dialog */}
      <Dialog open={bookingDialogOpen} onOpenChange={setBookingDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirmar reserva</DialogTitle>
            <DialogDescription>
              Estás por reservar {amenity.name}
            </DialogDescription>
          </DialogHeader>

          {selectedSlot && (
            <div className="space-y-4 py-4">
              <div className="rounded-xl bg-muted p-4">
                <div className="flex items-center gap-2">
                  <Calendar className="h-5 w-5 text-muted-foreground" />
                  <span className="font-medium">
                    {formatDate(selectedSlot.startTime, tz)}
                  </span>
                </div>
                <div className="mt-2 flex items-center gap-2">
                  <Clock className="h-5 w-5 text-muted-foreground" />
                  <span>
                    {formatTime(selectedSlot.startTime, tz)} -{" "}
                    {formatTime(selectedSlot.endTime, tz)}
                  </span>
                </div>
              </div>

              {amenity.requiresApproval && (
                <p className="text-sm text-muted-foreground">
                  Este amenity requiere aprobación. Tu reserva quedará pendiente
                  hasta ser aprobada.
                </p>
              )}
            </div>
          )}

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setBookingDialogOpen(false)}
              disabled={createReservation.isPending}
            >
              Cancelar
            </Button>
            <Button
              onClick={handleConfirmBooking}
              disabled={createReservation.isPending}
            >
              {createReservation.isPending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Reservando...
                </>
              ) : (
                "Confirmar reserva"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Cancel Reservation Confirmation */}
      <AlertDialog
        open={!!cancelReservationId}
        onOpenChange={(open) => {
          if (!open) setCancelReservationId(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Cancelar reserva?</AlertDialogTitle>
            <AlertDialogDescription>
              Esta acción no se puede deshacer. Tu turno quedará disponible para
              otros residentes.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Volver</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (cancelReservationId) {
                  cancelReservation.mutate({
                    reservationId: cancelReservationId,
                  });
                  setCancelReservationId(null);
                }
              }}
            >
              Cancelar reserva
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
